/**
 * CivicPulse i18n — 12 Indian languages + English.
 *
 * How it works:
 *   1. A canonical STRINGS map lives here — every civic UI string is
 *      keyed by a stable slug so we translate the same catalogue no
 *      matter which page loads first.
 *   2. `useCivicI18n()` exposes `{ lang, setLang, t, langs, isLoading }`.
 *      `t(key, fallback)` looks up the translated value; on miss it
 *      returns `fallback` (usually the English source).
 *   3. When `lang` changes to anything other than 'en', we batch-post
 *      every uncached string to `/api/i18n/translate` and cache the
 *      result in `localStorage['civic.i18n:<lang>']` so we never pay
 *      the LLM roundtrip twice for the same string.
 *   4. Fires 'civic-i18n:change' on window when lang flips, so any
 *      component watching for it can force a re-render.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "civic.i18n.lang";
const CACHE_PREFIX = "civic.i18n:";

// 12 Indian languages + English default. Order = drop-down order.
export const CIVIC_LANGUAGES = [
  { code: "en", label: "English",   native: "English" },
  { code: "hi", label: "Hindi",     native: "हिन्दी" },
  { code: "ml", label: "Malayalam", native: "മലയാളം" },
  { code: "ta", label: "Tamil",     native: "தமிழ்" },
  { code: "te", label: "Telugu",    native: "తెలుగు" },
  { code: "kn", label: "Kannada",   native: "ಕನ್ನಡ" },
  { code: "mr", label: "Marathi",   native: "मराठी" },
  { code: "bn", label: "Bengali",   native: "বাংলা" },
  { code: "gu", label: "Gujarati",  native: "ગુજરાતી" },
  { code: "pa", label: "Punjabi",   native: "ਪੰਜਾਬੀ" },
  { code: "or", label: "Odia",      native: "ଓଡ଼ିଆ" },
  { code: "as", label: "Assamese",  native: "অসমীয়া" },
  { code: "ur", label: "Urdu",      native: "اردو" },
];

// Canonical English strings. Keys are stable; NEVER change a key
// without invalidating the localStorage cache.
export const STRINGS = {
  // Header + nav
  "nav.map":            "Civic Map",
  "nav.report":         "Report",
  "nav.resolved":       "Resolved",
  "nav.communities":    "Communities",
  "nav.authorities":    "For Authorities",
  "tag.report":         "Report",
  "tag.connect":        "Connect",
  "tag.resolve":        "Resolve",
  "tag.improve":        "Improve",
  "search.label":       "Search",
  "language.label":     "Language",

  // Home
  "home.eyebrow":       "Stronger communities. Brighter tomorrows.",
  "home.headline1":     "See a problem in your",
  "home.headline2":     "community?",
  "home.headline3":     "Put it on the map.",
  "home.lead":          "Report local civic problems, connect with the responsible authority, follow the response, and help verify the solution.",
  "home.cta.report":    "Report a Problem",
  "home.cta.map":       "View Civic Map",
  "home.pillar.report_title":    "Citizens report",
  "home.pillar.report_sub":      "Real issues",
  "home.pillar.respond_title":   "Authorities respond",
  "home.pillar.respond_sub":     "Track progress",
  "home.pillar.communities_title": "Stronger communities",
  "home.pillar.communities_sub":   "A cleaner tomorrow",
  "home.map.footer":    "Click any pin to open the issue → follow, comment, verify.",
  "home.map.issues_on_map": "issues on the map",
  "home.map.resolved":  "resolved",
  "home.selector.country": "Country",
  "home.selector.state":   "State / Province",
  "home.selector.district": "District",
  "home.selector.city":    "City / Town",
  "home.selector.category": "Category",
  "home.recent_resolved": "Recently Resolved",
  "home.view_all":      "View All",
  "home.reported_issues": "reported issues",
  "home.first_resolutions": "First resolutions will appear here.",
  "home.verified_resolved": "Verified Resolved",

  // Report wizard
  "report.title":              "Report a Problem",
  "report.step1.title":        "Step 1 — Capture the problem",
  "report.step1.lead":         "Snap a photo or upload one from your gallery. Our AI Civic Assistant will try to identify the issue automatically — you can still edit everything.",
  "report.step1.picker":       "Take or choose a photo",
  "report.step1.picker_hint":  "JPEG, PNG, or WebP up to 8 MB",
  "report.step1.uploading":    "Uploading…",
  "report.step1.next":         "Next: locate on map",
  "report.step2.title":        "Step 2 — Pin the location",
  "report.step2.lead":         "We tried to auto-detect where you are. Tap or drag the marker to move it. This helps the responsible authority find the exact spot.",
  "report.step2.next":         "Next: describe the issue",
  "report.step2.back":         "Back",
  "report.step2.landmark":     "Landmark / address",
  "report.step2.district":     "District",
  "report.step2.city":         "City / Village",
  "report.step2.pinned":       "Pinned at",
  "report.step3.title":        "Step 3 — Describe & confirm",
  "report.step3.ai_working":   "AI Civic Assistant is analysing your photo…",
  "report.step3.ai_done":      "Suggested by AI — review and edit anything below.",
  "report.step3.fill_details": "Fill in the details below.",
  "report.step3.category":     "Category",
  "report.step3.category_placeholder": "Select a category…",
  "report.step3.title_field":  "Title",
  "report.step3.description":  "Description (optional)",
  "report.step3.description_placeholder": "Add any detail that helps identify the problem.",
  "report.step3.name":         "Your name (optional)",
  "report.step3.contact":      "Contact — email or phone (optional)",
  "report.step3.anonymous":    "Publish this report anonymously (your name will be hidden from the public page).",
  "report.step3.submit":       "Submit report",
  "report.step3.submitting":   "Submitting…",
  "report.step3.duplicates":   "similar reports nearby",
  "report.step3.duplicates_hint": "Instead of creating a duplicate, consider marking yourself as also affected on an existing one — it makes the issue harder to ignore.",
  "report.done.title":         "Thank you — it's on the map.",
  "report.done.id_label":      "Your permanent CivicPulse ID is",
  "report.done.view_issue":    "View your issue",
  "report.done.open_map":      "Open civic map",
  "report.done.info":          "We'll notify the appropriate authority. Follow the issue to receive updates. You can share the URL above so others can support it.",
  "report.emergency_warning":  "This may be an emergency — please dial 112 first.",
  "report.not_emergency":      "CivicPulse is not an emergency service. In an emergency, dial 112.",

  // Privacy chip
  "privacy.faces_blurred":     "faces blurred",
  "privacy.plates_blurred":    "plates blurred",
  "privacy.regions_blurred":   "regions blurred for privacy",
  "privacy.chip_hint":         "Faces and license plates are automatically blurred so people and vehicles in your photo stay anonymous.",
};

// Read the cached translation table for a language.
function readCache(lang) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + lang);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function writeCache(lang, table) {
  try { localStorage.setItem(CACHE_PREFIX + lang, JSON.stringify(table)); } catch { /* quota */ }
}

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ""}/api/i18n`;

async function translateOne(text, target) {
  const res = await fetch(`${API_ROOT}/translate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ text, target_lang: target, source_lang: "en" }),
  });
  if (!res.ok) throw new Error(`translate failed HTTP ${res.status}`);
  const j = await res.json();
  return j.translated || text;
}

// Simple in-flight promise map so overlapping renders don't fire duplicate requests.
const inflight = new Map();

async function ensureBatch(lang, keysToTranslate) {
  if (lang === "en" || !keysToTranslate.length) return {};
  const existing = readCache(lang);
  const missing = keysToTranslate.filter((k) => !existing[k]);
  if (!missing.length) return existing;

  const flightKey = `${lang}:${missing.length}`;
  if (inflight.has(flightKey)) return inflight.get(flightKey);

  const p = (async () => {
    const table = { ...existing };
    // Fire in parallel (each string is 5-20 words, cheap per-call).
    await Promise.all(missing.map(async (key) => {
      try {
        const out = await translateOne(STRINGS[key], lang);
        table[key] = out;
      } catch {
        table[key] = STRINGS[key];   // fail-open: keep English
      }
    }));
    writeCache(lang, table);
    return table;
  })();
  inflight.set(flightKey, p);
  try { return await p; } finally { inflight.delete(flightKey); }
}

let _globalLang = null;
function getInitialLang() {
  if (_globalLang) return _globalLang;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && CIVIC_LANGUAGES.some((l) => l.code === stored)) {
      _globalLang = stored;
      return stored;
    }
  } catch { /* ignore */ }
  _globalLang = "en";
  return "en";
}

export function useCivicI18n() {
  const [lang, setLangState] = useState(getInitialLang);
  const [table, setTable] = useState(() => lang === "en" ? {} : readCache(lang));
  const [isLoading, setIsLoading] = useState(false);

  // Cross-component sync — respond to lang changes elsewhere.
  useEffect(() => {
    function onChange(e) {
      const next = e?.detail?.lang;
      if (next && next !== lang) setLangState(next);
    }
    window.addEventListener("civic-i18n:change", onChange);
    return () => window.removeEventListener("civic-i18n:change", onChange);
  }, [lang]);

  // Load / hydrate the table whenever lang changes.
  useEffect(() => {
    if (lang === "en") { setTable({}); return; }
    const cached = readCache(lang);
    setTable(cached);
    const needed = Object.keys(STRINGS).filter((k) => !cached[k]);
    if (!needed.length) return;
    setIsLoading(true);
    ensureBatch(lang, needed)
      .then((full) => setTable(full))
      .finally(() => setIsLoading(false));
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!CIVIC_LANGUAGES.some((l) => l.code === next)) return;
    _globalLang = next;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    setLangState(next);
    window.dispatchEvent(new CustomEvent("civic-i18n:change", { detail: { lang: next } }));
  }, []);

  const t = useCallback((key, fallback) => {
    if (lang === "en") return fallback ?? STRINGS[key] ?? key;
    return table[key] || fallback || STRINGS[key] || key;
  }, [lang, table]);

  return useMemo(() => ({
    lang, setLang, t, langs: CIVIC_LANGUAGES, isLoading,
  }), [lang, setLang, t, isLoading]);
}
